import type { DirectoryEntry, FieldDefinition } from "@estelle/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Loader2, Tag as TagIcon } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { BulkProgress, BulkReport } from "@/components/table/BulkReport";
import { csvFilename, downloadCsv, toCsv } from "@/components/table/csv";
import { useBulkRun } from "@/components/table/useBulkRun";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DIRECTORY_KEYS } from "@/modules/contacts/directory/keys";
import { projectDirectoryEntry } from "@/modules/contacts/directory/projections";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";

type Mode = "add" | "remove";

/**
 * The bulk actions on All Contacts (#634): add or remove a tag, and export the
 * selected rows. Ankur named these two; there are no others on purpose.
 *
 * A tag goes through each row's own write — a contact's tag list, or the
 * team's tag on a Shopify customer — a few at a time, so one refusal changes
 * nothing for that row and the rest carry on. Each write still checks its own
 * permission on the server; the buttons only hide what this person cannot do.
 */
export function DirectoryBulkActions({
  entries,
  fields,
}: {
  entries: DirectoryEntry[];
  /** The columns on screen, for the export. */
  fields: FieldDefinition[];
}) {
  const permissions = usePermissions();
  const mayTagContacts = can(permissions, "contacts.contact.edit");
  const mayTagCustomers = can(permissions, "customers.annotate");

  return (
    <>
      {mayTagContacts || mayTagCustomers ? (
        <TagAction
          entries={entries.filter((entry) =>
            entry.kind === "contact" ? mayTagContacts : mayTagCustomers,
          )}
        />
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="max-md:min-h-11"
        onClick={() => {
          downloadCsv(
            csvFilename(
              "contacts",
              "selected",
              new Date(reportingNow()).toISOString(),
            ),
            toCsv(fields, entries.map(projectDirectoryEntry)),
          );
        }}
      >
        <Download />
        Export selected
      </Button>
    </>
  );
}

function TagAction({ entries }: { entries: DirectoryEntry[] }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [tag, setTag] = useState("");
  const clean = tag.trim();

  const bulk = useBulkRun<DirectoryEntry>((entry) => {
    if (entry.kind === "customer") {
      return mode === "add"
        ? data.customers.addTag(entry.id, clean)
        : data.customers.removeTag(entry.id, clean);
    }
    const current = entry.tags;
    const next =
      mode === "add"
        ? [...new Set([...current, clean])]
        : current.filter(
            (existing) => existing.toLowerCase() !== clean.toLowerCase(),
          );
    return data.contacts.update(entry.id, { tags: next });
  });

  async function apply(retry = false) {
    if (retry) await bulk.retryFailed();
    else await bulk.run(entries);
    void queryClient.invalidateQueries({ queryKey: DIRECTORY_KEYS.list });
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setTag("");
          bulk.reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="max-md:min-h-11"
          disabled={entries.length === 0}
        >
          <TagIcon />
          Tag
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3">
        <div
          className="bg-muted inline-flex rounded-md p-0.5"
          role="radiogroup"
        >
          {(["add", "remove"] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={mode === option}
              onClick={() => {
                setMode(option);
                bulk.reset();
              }}
              className={cn(
                "rounded px-3 py-1 text-sm",
                mode === option && "bg-background font-medium shadow-sm",
              )}
            >
              {option === "add" ? "Add a tag" : "Remove a tag"}
            </button>
          ))}
        </div>
        <div className="space-y-1">
          <Label htmlFor="bulk-contact-tag">
            {mode === "add" ? "Tag to add" : "Tag to remove"} on{" "}
            {entries.length} {entries.length === 1 ? "contact" : "contacts"}
          </Label>
          <Input
            id="bulk-contact-tag"
            value={tag}
            onChange={(event) => {
              setTag(event.target.value);
            }}
            placeholder="e.g. wholesale"
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={clean === "" || bulk.running}
          onClick={() => {
            void apply();
          }}
        >
          {bulk.running ? <Loader2 className="animate-spin" /> : null}
          {mode === "add" ? "Add" : "Remove"}
        </Button>
        {bulk.progress ? <BulkProgress progress={bulk.progress} /> : null}
        {bulk.outcomes && !bulk.progress ? (
          <BulkReport
            outcomes={bulk.outcomes}
            labelOf={(entry) => entry.displayName || entry.id}
            verb={mode === "add" ? "tagged" : "untagged"}
            retrying={bulk.running}
            onRetry={() => {
              void apply(true);
            }}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
